import { readContract, WriteContractParameters } from '@wagmi/core';
import { Address, erc20Abi } from 'viem';

import { BUY_TOKEN, SELL_TOKEN } from '@/app/components/LlamaSwap/constants';
import {
  buildSignedSwapSendTx,
  NormalizedQuote,
  type SwapSendTx,
} from '@/app/components/LlamaSwap/helpers';
import { config } from '@/app/providers/app/wagmiConfig';
import { WYLDS_ALLOWANCE_INSUFFICIENT_MESS } from '@/app/shared/constants/errors';
import { ChainContracts } from '@/app/shared/constants/web3/chainContracts';
import { ABI, WYLDS } from '@/app/shared/constants/web3/constants';
import { SequentialStepPlan } from '@/app/shared/hooks/blockchain/useSendSequentialSteps/useSendSequentialSteps';
import { makeApproveTx } from '@/app/shared/lib/blockchain/makeApproveTx';

const MZUSD_TO_ASSET_SCALE = 10n ** 12n;
const SLIPPAGE_FLOOR_BPS = 99n;
const SWAP_SURPLUS_CEIL_BPS = 10050n;

const minBigInt = (a: bigint, b: bigint) => (a < b ? a : b);

const deficit = (target: bigint, owned: bigint) => (target > owned ? target - owned : 0n);

const mzusdToAsset = (mzusdAmount: bigint) => mzusdAmount / MZUSD_TO_ASSET_SCALE;

const assetToMzusd = (assetAmount: bigint) => assetAmount * MZUSD_TO_ASSET_SCALE;

const withSlippageFloor = (amount: bigint) => (amount * SLIPPAGE_FLOOR_BPS) / 100n;

const withSwapSurplusCeil = (amount: bigint) => (amount * SWAP_SURPLUS_CEIL_BPS) / 10000n;

type SignTypedDataFn = Parameters<typeof buildSignedSwapSendTx>[1];

export type SwapDepositBalances = {
  mzusd: bigint;
  wylds: bigint;
  wyldsAllowance: bigint;
  mzusdAllowance: bigint;
};

export type SwapDepositSession = {
  wyldsBeforeSwap: { current: bigint | null };
  receivedWylds: { current: bigint | null };
  quote: { current: NormalizedQuote | null };
};

export type SwapDepositPlanInput = {
  address: Address;
  contracts: ChainContracts;
  trancheAddress: Address;
  trancheSymbol: string;
  providerLabel: string;
  amountUsdc: bigint;
  amountMzusd: bigint;
  quotedWylds: bigint;
  quote: NormalizedQuote;
  balances: SwapDepositBalances;
  swapConfirmed: boolean;
  session: SwapDepositSession;
  signTypedData: SignTypedDataFn;
  refetchWyldsAllowance: () => Promise<unknown>;
};

export type SwapDepositProgress = {
  hasEnoughMzusd: boolean;
  hasEnoughWylds: boolean;
  swapDone: boolean;
  mintWyldsAmount: bigint;
  depositAmount: bigint;
  expectedWylds: bigint;
  swapSpender: Address;
};

export function computeSwapDepositProgress({
  amountUsdc,
  amountMzusd,
  quotedWylds,
  quote,
  balances,
  swapConfirmed,
}: Pick<
  SwapDepositPlanInput,
  'amountUsdc' | 'amountMzusd' | 'quotedWylds' | 'quote' | 'balances' | 'swapConfirmed'
>): SwapDepositProgress {
  const needMzusd = deficit(amountMzusd, balances.mzusd);
  const needWylds = needMzusd > 0n ? mzusdToAsset(needMzusd) : 0n;
  const hasEnoughMzusd = needMzusd === 0n;

  const minWyldsToSkipSwap = needWylds > 0n ? withSlippageFloor(needWylds) : 0n;
  const hasEnoughWylds =
    hasEnoughMzusd || (minWyldsToSkipSwap > 0n && balances.wylds >= minWyldsToSkipSwap);

  const mintWyldsAmount = needWylds > 0n ? minBigInt(balances.wylds, needWylds) : 0n;
  const depositAmount = hasEnoughMzusd
    ? minBigInt(balances.mzusd, amountMzusd)
    : amountMzusd;
  const expectedWylds =
    quotedWylds > 0n ? quotedWylds : needWylds > 0n ? needWylds : amountUsdc;

  const swapSpender = (quote.tokenApprovalAddress || quote.tx?.to || '') as Address;
  const swapDone = hasEnoughWylds || hasEnoughMzusd || swapConfirmed;

  return {
    hasEnoughMzusd,
    hasEnoughWylds,
    swapDone,
    mintWyldsAmount,
    depositAmount,
    expectedWylds,
    swapSpender,
  };
}

async function readTokenBalance(token: Address, owner: Address): Promise<bigint> {
  return readContract(config, {
    address: token,
    abi: erc20Abi,
    functionName: 'balanceOf',
    args: [owner],
  });
}

async function readTokenAllowance(
  token: Address,
  owner: Address,
  spender: Address,
): Promise<bigint> {
  return readContract(config, {
    address: token,
    abi: erc20Abi,
    functionName: 'allowance',
    args: [owner, spender],
  });
}

async function resolveMintAssetAmount(input: SwapDepositPlanInput): Promise<bigint> {
  const { address, contracts, amountMzusd, session } = input;
  const liveWylds = await readTokenBalance(WYLDS, address);

  const sessionReceived = session.receivedWylds.current;
  if (sessionReceived !== null && sessionReceived > 0n) {
    return minBigInt(sessionReceived, liveWylds);
  }

  const beforeSwap = session.wyldsBeforeSwap.current;
  if (beforeSwap !== null && liveWylds > beforeSwap) {
    const delta = liveWylds - beforeSwap;
    session.receivedWylds.current = delta;
    return delta;
  }

  const liveMzusd = await readTokenBalance(contracts.MZUSD, address);
  const stillNeedMzusd = deficit(amountMzusd, liveMzusd);
  const stillNeedWylds = stillNeedMzusd > 0n ? mzusdToAsset(stillNeedMzusd) : 0n;

  if (stillNeedWylds <= 0n || liveWylds < stillNeedWylds) {
    throw new Error('Insufficient wYLDS balance after swap');
  }

  const surplusCap = withSwapSurplusCeil(stillNeedWylds);
  return liveWylds <= surplusCap ? liveWylds : stillNeedWylds;
}

async function resolveDepositMzusdAmount(input: SwapDepositPlanInput): Promise<bigint> {
  const { address, contracts, amountMzusd, session } = input;
  const liveMzusd = await readTokenBalance(contracts.MZUSD, address);

  if (liveMzusd <= 0n) {
    throw new Error('Insufficient MZUSD balance for deposit');
  }

  const sessionReceived = session.receivedWylds.current;
  if (sessionReceived !== null && sessionReceived > 0n) {
    return minBigInt(liveMzusd, assetToMzusd(sessionReceived));
  }

  const surplusCap = withSwapSurplusCeil(amountMzusd);
  if (liveMzusd <= surplusCap) {
    return liveMzusd;
  }

  return minBigInt(liveMzusd, amountMzusd);
}

async function resolveApproveWyldsTx(input: SwapDepositPlanInput): Promise<WriteContractParameters> {
  const { contracts } = input;
  const amount = await resolveMintAssetAmount(input);

  if (amount <= 0n) {
    throw new Error('Insufficient wYLDS balance after swap');
  }

  return makeApproveTx({
    token: contracts.ASSET,
    spender: contracts.MZUSD,
    amount,
  }) as WriteContractParameters;
}

async function resolveMintTx(input: SwapDepositPlanInput): Promise<WriteContractParameters> {
  const { address, contracts, refetchWyldsAllowance } = input;
  const assetAmount = await resolveMintAssetAmount(input);
  const allowance = await readTokenAllowance(contracts.ASSET, address, contracts.MZUSD);

  if (allowance < assetAmount) {
    await refetchWyldsAllowance();
    throw new Error(WYLDS_ALLOWANCE_INSUFFICIENT_MESS);
  }

  return {
    address: contracts.MZUSD,
    abi: ABI.MZUSD,
    functionName: 'deposit',
    args: [assetAmount],
  };
}

async function resolveApproveMzusdTx(input: SwapDepositPlanInput): Promise<WriteContractParameters> {
  const { contracts, trancheAddress } = input;
  const amount = await resolveDepositMzusdAmount(input);

  if (amount <= 0n) {
    throw new Error('Insufficient MZUSD balance for deposit');
  }

  return makeApproveTx({
    token: contracts.MZUSD,
    spender: trancheAddress,
    amount,
  }) as WriteContractParameters;
}

async function resolveDepositTx(input: SwapDepositPlanInput): Promise<WriteContractParameters> {
  const { address, trancheAddress } = input;
  const liveDeposit = await resolveDepositMzusdAmount(input);

  if (liveDeposit <= 0n) {
    throw new Error('Insufficient MZUSD balance for deposit');
  }

  return {
    address: trancheAddress,
    abi: ABI.TRANCHE,
    functionName: 'deposit',
    args: [liveDeposit, address],
  };
}

export const DEPOSIT_ATOMIC_BATCH_STEP_IDS = [
  'approve-wylds',
  'mint-mzusd',
  'approve-mzusd',
  'deposit-tranche',
] as const;

export async function buildDepositAtomicBatchTxs(
  input: SwapDepositPlanInput,
  pendingStepIds: string[],
): Promise<WriteContractParameters[]> {
  const { address, contracts, trancheAddress } = input;
  const pending = new Set(pendingStepIds);
  const progress = computeSwapDepositProgress(input);
  const needsDeposit = pending.has('deposit-tranche') || pending.has('approve-mzusd');
  const needsMint = pending.has('mint-mzusd') || pending.has('approve-wylds');
  const txs: WriteContractParameters[] = [];

  if (progress.hasEnoughMzusd || (!needsMint && needsDeposit)) {
    const depositAmount = await resolveDepositMzusdAmount(input);

    txs.push(
      makeApproveTx({
        token: contracts.MZUSD,
        spender: trancheAddress,
        amount: depositAmount,
      }) as WriteContractParameters,
    );
    txs.push({
      address: trancheAddress,
      abi: ABI.TRANCHE,
      functionName: 'deposit',
      args: [depositAmount, address],
    });
    return txs;
  }

  const mintAmount = await resolveMintAssetAmount(input);
  if (mintAmount <= 0n) {
    throw new Error('Insufficient wYLDS balance after swap');
  }

  const depositAmount = assetToMzusd(mintAmount);

  txs.push(
    makeApproveTx({
      token: contracts.ASSET,
      spender: contracts.MZUSD,
      amount: mintAmount,
    }) as WriteContractParameters,
  );
  txs.push({
    address: contracts.MZUSD,
    abi: ABI.MZUSD,
    functionName: 'deposit',
    args: [mintAmount],
  });
  txs.push(
    makeApproveTx({
      token: contracts.MZUSD,
      spender: trancheAddress,
      amount: depositAmount,
    }) as WriteContractParameters,
  );
  txs.push({
    address: trancheAddress,
    abi: ABI.TRANCHE,
    functionName: 'deposit',
    args: [depositAmount, address],
  });

  return txs;
}

async function resolveSwapSendTx(input: SwapDepositPlanInput): Promise<SwapSendTx> {
  const { address, session, signTypedData } = input;

  const before = await readTokenBalance(WYLDS, address);
  session.wyldsBeforeSwap.current = before;
  session.receivedWylds.current = null;

  const liveQuote = session.quote.current;
  if (!liveQuote) throw new Error('Swap quote is not available');

  return buildSignedSwapSendTx(liveQuote, signTypedData);
}

export function buildUsdcSwapDepositSteps(input: SwapDepositPlanInput): SequentialStepPlan[] {
  const { address, contracts, trancheAddress, trancheSymbol, providerLabel, amountUsdc, session } =
    input;

  const progress = computeSwapDepositProgress(input);
  const { hasEnoughMzusd, swapDone, mintWyldsAmount, depositAmount, expectedWylds, swapSpender } =
    progress;

  const sessionReceived = session.receivedWylds.current;
  const mintPreviewAmount =
    sessionReceived !== null && sessionReceived > 0n
      ? sessionReceived
      : mintWyldsAmount > 0n
        ? mintWyldsAmount
        : expectedWylds;
  const depositPreviewAmount =
    sessionReceived !== null && sessionReceived > 0n
      ? assetToMzusd(sessionReceived)
      : depositAmount;

  return [
    {
      id: 'approve-swap',
      sectionId: 'swap',
      title: 'Accept Spending for Swap',
      description: `Approve ${SELL_TOKEN.symbol} spending for the swap contract`,
      isComplete: swapDone,
      tx: makeApproveTx({
        token: SELL_TOKEN.address as Address,
        spender: swapSpender,
        amount: amountUsdc,
      }) as WriteContractParameters,
    },
    {
      id: 'swap-usdc',
      sectionId: 'swap',
      title: 'Swap Transaction',
      description: `Swap ${SELL_TOKEN.symbol} into ${BUY_TOKEN.symbol} via ${providerLabel}`,
      isComplete: swapDone,
      resolveSendTx: () => resolveSwapSendTx(input),
    },
    {
      id: 'approve-wylds',
      sectionId: 'deposit',
      title: 'Accept Spending to mint mzUSD',
      description: `Approve ${BUY_TOKEN.symbol} spending for the MZUSD contract`,
      isComplete: hasEnoughMzusd,
      resolveTx: () => resolveApproveWyldsTx(input),
      tx: makeApproveTx({
        token: contracts.ASSET,
        spender: contracts.MZUSD,
        amount: mintPreviewAmount,
      }) as WriteContractParameters,
    },
    {
      id: 'mint-mzusd',
      sectionId: 'deposit',
      title: 'Mint mzUSD',
      description: `Deposit ${BUY_TOKEN.symbol} to receive MZUSD`,
      isComplete: hasEnoughMzusd,
      resolveTx: () => resolveMintTx(input),
      tx: {
        address: contracts.MZUSD,
        abi: ABI.MZUSD,
        functionName: 'deposit',
        args: [mintPreviewAmount],
      },
    },
    {
      id: 'approve-mzusd',
      sectionId: 'deposit',
      title: 'Accept Spending to Deposit',
      description: `Approve MZUSD spending for the ${trancheSymbol} contract`,
      isComplete: false,
      resolveTx: () => resolveApproveMzusdTx(input),
      tx: makeApproveTx({
        token: contracts.MZUSD,
        spender: trancheAddress,
        amount: depositPreviewAmount,
      }) as WriteContractParameters,
    },
    {
      id: 'deposit-tranche',
      sectionId: 'deposit',
      title: 'Deposit to Tranche',
      description: `Deposit MZUSD to receive ${trancheSymbol}`,
      isComplete: false,
      resolveTx: () => resolveDepositTx(input),
      tx: {
        address: trancheAddress,
        abi: ABI.TRANCHE,
        functionName: 'deposit',
        args: [depositPreviewAmount, address],
      },
    },
  ];
}
